"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { uploadApi, usersApi, workspacesApi, Workspace } from "@/lib/api";
import { WorkspaceEditSummaryCard } from "@/app/components/WorkspaceEditSummaryCard";
import {
  WorkspaceCreateOptionsSection,
  WorkspaceCreatePrivacyNote,
} from "@/app/components/WorkspaceCreateFormFields";
import {
  WORKSPACE_NAME_ICON_EDIT_LABEL,
  WorkspaceNameIconField,
} from "@/app/components/WorkspaceNameIconField";
import {
  DEFAULT_WORKSPACE_AVATAR,
  normalizeWorkspace,
  resolveWorkspaceAvatarFromWorkspace,
  resolveWorkspaceImageDisplayUrl,
  resolveWorkspaceImageUrlForSave,
  unwrapWorkspaceResponse,
  type WorkspaceAvatarSelection,
} from "@/lib/workspaceAvatar";
import { syncWorkspaceAvatarTagsFromWorkspace } from "@/lib/workspaceAvatarTags";
import { useWorkspaceCreation } from "@/hooks/useWorkspaceCreation";
import { apiGet } from "@/lib/ApiFetch";
import {
  getDefaultAeraBusinessOption,
  resolveBusinessTypeFromConfig,
} from "@/app/components/AeraBusinessOptionsGrid";
import { AeraButton } from "@/app/components/ui/AeraButton";
import Modal from "@/app/components/ui/Modal";

type WorkspaceTag = {
  id?: string;
  key?: string;
  value?: string;
  createdAt?: string;
  updatedAt?: string;
  [key: string]: unknown;
};

type WorkspaceWithMeta = Workspace & {
  imageUrl?: string | null;
  storeId?: string | null;
  ownerId?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  tags?: WorkspaceTag[] | null;
  [key: string]: unknown;
};

type OwnerApiUser = {
  email?: string;
  name?: string;
  displayName?: string;
  avatar?: string;
  image?: string;
  profile?: {
    displayName?: string;
    avatar?: string;
  };
};

const UNIQUE_WORKSPACE_TAG_KEYS = new Set([
  "business_type_config",
  "business_type_display",
  "status",
  "scale",
]);

function pickUniqueWorkspaceTag<
  T extends { id?: string; key: string; value: string },
>(tags: T[], key: string): T | undefined {
  const matches = tags.filter((t) => t.key === key);
  if (matches.length === 0) return undefined;
  const preferred = matches[matches.length - 1]!;
  if (preferred.id) return preferred;
  const withId = matches.find((t) => t.id);
  return withId ? { ...preferred, id: withId.id } : preferred;
}

async function upsertWorkspaceTagByKey(
  workspaceId: string,
  key: string,
  value: string,
  originalTags: WorkspaceTag[],
): Promise<void> {
  const k = key.trim();
  const v = value.trim();
  if (!k || !v) return;

  const rows = (originalTags ?? []).filter(
    (t) => String(t.key ?? "").trim() === k,
  );
  if (rows.length === 1 && String(rows[0]?.value ?? "").trim() === v) return;

  for (const row of rows) {
    const rowValue = String(row.value ?? "").trim();
    try {
      if (rowValue) {
        await workspacesApi.tags.deleteByKey(workspaceId, k, rowValue);
      } else {
        await workspacesApi.tags.deleteByKey(workspaceId, k);
      }
    } catch {
      // ignore
    }
  }

  try {
    await workspacesApi.tags.deleteByKey(workspaceId, k);
  } catch {
    // ignore
  }

  try {
    await workspacesApi.tags.create(workspaceId, { key: k, value: v });
  } catch (err) {
    if (isDuplicateKeyError(err)) return;
    throw err;
  }
}

function dedupeWorkspaceTags<
  T extends { id?: string; key: string; value: string },
>(tags: T[]): T[] {
  const uniqueKeySet = UNIQUE_WORKSPACE_TAG_KEYS;
  const rest = tags.filter((tag) => !uniqueKeySet.has(tag.key));

  return [
    ...rest,
    ...[...UNIQUE_WORKSPACE_TAG_KEYS].flatMap((key) => {
      const tag = pickUniqueWorkspaceTag(tags, key);
      return tag ? [tag] : [];
    }),
  ];
}

function SkeletonBlock({ className }: { className: string }) {
  return (
    <div className={["rounded-md bg-aera-lightgray/70", className].join(" ")} />
  );
}

function WorkspaceModalSkeleton({ mode }: { mode: "create" | "edit" }) {
  return (
    <div className="animate-pulse space-y-6">
      <div className="space-y-4 rounded-xl border border-gray-100 bg-aera-white p-4">
        <div className="flex items-center gap-3">
          <SkeletonBlock className="h-12 w-12 rounded-xl" />
          <div className="min-w-0 flex-1 space-y-2">
            <SkeletonBlock className="h-4 w-2/3" />
            <SkeletonBlock className="h-3 w-1/2" />
          </div>
        </div>
        {mode === "edit" ? (
          <div className="space-y-2.5 pt-1">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-center gap-3">
                <SkeletonBlock className="h-9 w-9 rounded-lg shrink-0" />
                <div className="flex-1 space-y-1.5">
                  <SkeletonBlock className="h-2.5 w-12" />
                  <SkeletonBlock className="h-3.5 w-32" />
                </div>
              </div>
            ))}
            <SkeletonBlock className="h-9 w-full rounded-lg mt-2" />
          </div>
        ) : null}
      </div>

      <div className="space-y-4 rounded-xl border border-gray-100 bg-gray-50 p-4">
        <div className="space-y-2">
          <SkeletonBlock className="h-3 w-24" />
          <SkeletonBlock className="h-12 w-full rounded-xl" />
        </div>

        {mode === "edit" ? (
          <div className="space-y-2">
            <SkeletonBlock className="h-3 w-40" />
            <div className="grid grid-cols-2 gap-1.5">
              <SkeletonBlock className="h-16 w-full rounded-lg" />
              <SkeletonBlock className="h-16 w-full rounded-lg" />
              <SkeletonBlock className="h-16 w-full rounded-lg" />
              <SkeletonBlock className="h-16 w-full rounded-lg" />
            </div>
          </div>
        ) : null}
      </div>

      <div className="rounded-xl border border-gray-100 bg-gray-50 p-4">
        <div className="flex items-center gap-4">
          <SkeletonBlock className="h-16 w-16 rounded-xl" />
          <div className="flex-1 space-y-2">
            <SkeletonBlock className="h-9 w-full rounded-lg" />
            <SkeletonBlock className="h-3 w-2/3" />
          </div>
        </div>
      </div>
    </div>
  );
}

function unwrapData<T>(data: unknown): T | null {
  if (!data || typeof data !== "object") return null;
  const maybe = data as {
    data?: unknown;
    workspace?: unknown;
    workspaces?: unknown;
    user?: unknown;
  };
  return ((maybe.data ?? maybe.workspace ?? maybe.user ?? data) as T) ?? null;
}

function unwrapTags(data: unknown): WorkspaceTag[] {
  if (Array.isArray(data)) return data as WorkspaceTag[];
  if (!data || typeof data !== "object") return [];
  const obj = data as { tags?: unknown; data?: unknown };
  const innerData =
    obj.data && typeof obj.data === "object"
      ? (obj.data as { tags?: unknown })
      : null;
  const inner = obj.tags ?? innerData?.tags ?? null;
  return Array.isArray(inner) ? (inner as WorkspaceTag[]) : [];
}

function extractOwnerUser(data: unknown): OwnerApiUser | null {
  const unwrapped = unwrapData<unknown>(data);
  if (!unwrapped || typeof unwrapped !== "object") return null;

  // common response: { user: { ... }, services: [] }
  const asObj = unwrapped as { user?: unknown };
  if (asObj.user && typeof asObj.user === "object") {
    return asObj.user as OwnerApiUser;
  }

  // fallback: response is the user itself
  return unwrapped as OwnerApiUser;
}

function formatDateTime(dt?: string | null): string {
  if (!dt) return "-";
  const d = new Date(dt);
  if (Number.isNaN(d.getTime())) return String(dt);
  return new Intl.DateTimeFormat("th-TH", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(d);
}

function resolveLatestWorkspaceUpdatedAt(
  workspace?: {
    updatedAt?: string | null;
    tags?: WorkspaceTag[] | null;
  } | null,
): string | null {
  if (!workspace) return null;

  let latestTs = 0;
  let latestValue: string | null = null;

  const consider = (value?: string | null) => {
    if (!value) return;
    const ts = Date.parse(value);
    if (Number.isNaN(ts) || ts <= latestTs) return;
    latestTs = ts;
    latestValue = value;
  };

  consider(workspace.updatedAt ?? null);
  for (const tag of workspace.tags ?? []) {
    consider(tag?.updatedAt ?? null);
  }

  return latestValue;
}

function formatStatusLabel(value: string): string | null {
  const v = value.trim().toLowerCase();
  if (!v) return null;
  if (v === "active") return "เปิดใช้งาน";
  if (v === "inactive") return "ปิดใช้งาน";
  return value.trim();
}

function isNoChangesError(err: unknown): boolean {
  if (!err || typeof err !== "object") return false;
  const maybe = err as { message?: unknown; data?: unknown };
  if (typeof maybe.message === "string" && maybe.message.includes("no_changes"))
    return true;
  const data = maybe.data;
  if (!data || typeof data !== "object") return false;
  const d = data as { error?: unknown; details?: unknown };
  if (typeof d.error === "string" && d.error.includes("no_changes"))
    return true;
  const details = d.details;
  if (details && typeof details === "object") {
    const det = details as { error?: unknown };
    if (typeof det.error === "string" && det.error.includes("no_changes"))
      return true;
  }
  return false;
}

function isDuplicateKeyError(err: unknown): boolean {
  if (!err || typeof err !== "object") return false;
  const maybe = err as { message?: unknown; data?: unknown };
  if (
    typeof maybe.message === "string" &&
    maybe.message.includes("duplicate_key_value")
  ) {
    return true;
  }
  const data = maybe.data;
  if (!data || typeof data !== "object") return false;
  const d = data as { error?: unknown; details?: unknown };
  if (typeof d.error === "string" && d.error.includes("duplicate_key_value"))
    return true;
  const details = d.details;
  if (details && typeof details === "object") {
    const det = details as { error?: unknown };
    if (
      typeof det.error === "string" &&
      det.error.includes("duplicate_key_value")
    )
      return true;
  }
  return false;
}

interface WorkspaceListModalProps {
  isOpen: boolean;
  mode: "create" | "edit";
  initialWorkspaceId?: string | null;
  onClose: () => void;
  onSuccess?: (workspaceId?: string) => void;
}

export default function WorkspaceListModal({
  isOpen,
  mode,
  initialWorkspaceId,
  onClose,
  onSuccess,
}: WorkspaceListModalProps) {
  const [workspaces, setWorkspaces] = useState<WorkspaceWithMeta[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [editTags, setEditTags] = useState<
    Array<{ id?: string; key: string; value: string }>
  >([]);
  const [createTags, setCreateTags] = useState<
    Array<{ key: string; value: string }>
  >([
    { key: "status", value: "active" },
    { key: "scale", value: "Solo" },
    // Business type intentionally unset until user selects.
  ]);
  const editHydratedWorkspaceIdRef = useRef<string | null>(null);
  const [editAvatar, setEditAvatar] = useState<WorkspaceAvatarSelection>(
    DEFAULT_WORKSPACE_AVATAR,
  );
  const [savingEdit, setSavingEdit] = useState(false);
  const [ownerProfile, setOwnerProfile] = useState<{
    displayName: string;
    avatarUrl: string | null;
  } | null>(null);
  const [ownerProfileLoading, setOwnerProfileLoading] = useState(false);

  const getTagValue = useCallback(
    (key: string): string => {
      const matches = editTags.filter((t) => t.key === key);
      if (matches.length === 0) return "";
      return matches[matches.length - 1]!.value;
    },
    [editTags],
  );

  const setTagValue = useCallback((key: string, value: string) => {
    setEditTags((prev) => {
      const existing = pickUniqueWorkspaceTag(prev, key);
      const without = prev.filter((t) => t.key !== key);
      if (!existing) return [...without, { key, value }];
      return [...without, { ...existing, key, value }];
    });
  }, []);

  const getCreateTagValue = useCallback(
    (key: string): string => createTags.find((t) => t.key === key)?.value ?? "",
    [createTags],
  );

  const setCreateTagValue = useCallback((key: string, value: string) => {
    setCreateTags((prev) => {
      const i = prev.findIndex((t) => t.key === key);
      if (i === -1) return [...prev, { key, value }];
      const without = prev.filter((t) => t.key !== key);
      const existing = prev[i]!;
      return [...without, { ...existing, key, value }];
    });
  }, []);

  const selectedCreateBusinessType = resolveBusinessTypeFromConfig(
    getCreateTagValue("business_type_config"),
  );
  const selectedEditBusinessType = resolveBusinessTypeFromConfig(
    getTagValue("business_type_config"),
  );

  const fetchWorkspaces = useCallback(
    async (opts?: { silent?: boolean }) => {
      try {
        if (!opts?.silent) setLoading(true);
        setError(null);
        if (mode === "edit") {
          const targetId = initialWorkspaceId ?? null;
          if (!targetId) {
            setWorkspaces([]);
            setError("ไม่พบ workspaceId สำหรับการแก้ไข");
            return;
          }
          const wsRaw = await workspacesApi.getById(targetId);
          const ws = unwrapData<WorkspaceWithMeta>(wsRaw);
          if (!ws) {
            setWorkspaces([]);
            setError("ไม่พบเวิร์กสเปซนี้ กรุณาลองใหม่อีกครั้ง");
            return;
          }

          const tagsRaw = await workspacesApi.tags.list(targetId);
          const tagsList = unwrapTags(tagsRaw);

          setWorkspaces([normalizeWorkspace({ ...ws, tags: tagsList })]);
          return;
        } else {
          const data = await workspacesApi.list();
          const workspaceList: WorkspaceWithMeta[] = Array.isArray(data)
            ? (data as WorkspaceWithMeta[])
            : Array.isArray(
                  (data as { data?: unknown } | null | undefined)?.data,
                )
              ? (((data as { data?: WorkspaceWithMeta[] } | null | undefined)
                  ?.data ?? []) as WorkspaceWithMeta[])
              : Array.isArray(
                    (data as { workspaces?: unknown } | null | undefined)
                      ?.workspaces,
                  )
                ? (((
                    data as
                      | { workspaces?: WorkspaceWithMeta[] }
                      | null
                      | undefined
                  )?.workspaces ?? []) as WorkspaceWithMeta[])
                : [];
          setWorkspaces(workspaceList);
        }
      } catch (err) {
        console.error("Failed to fetch workspaces:", err);
        setError("โหลดเวิร์กสเปซไม่สำเร็จ กรุณาลองใหม่อีกครั้ง");
      } finally {
        if (!opts?.silent) setLoading(false);
      }
    },
    [mode, initialWorkspaceId],
  );

  const {
    setIsCreating,
    newWorkspaceName,
    setNewWorkspaceName,
    creatingLoading,
    avatar: createAvatar,
    setAvatar: setCreateAvatar,
    uploadingImage,
    handleCreateWorkspace,
  } = useWorkspaceCreation({
    onSuccess: async (workspaceId) => {
      if (workspaceId && mode === "create") {
        const normalized = createTags
          .map((t) => ({ key: t.key.trim(), value: t.value.trim() }))
          .filter((t) => t.key && t.value);
        if (normalized.length) {
          try {
            await workspacesApi.tags.create(workspaceId, normalized);
          } catch (e) {
            console.warn("Failed to set workspace tags after create", e);
          }
        }
      }
      fetchWorkspaces();
      setIsCreating(false);
      if (onSuccess) onSuccess(workspaceId);
    },
    onError: (err) => setError(err),
  });

  useEffect(() => {
    if (!isOpen) {
      setLoading(false);
      return;
    }

    setIsCreating(mode === "create");
    setError(null);

    if (mode === "create") {
      setLoading(false);
      return;
    }

    setLoading(true);
    setWorkspaces([]);
    void fetchWorkspaces();
  }, [isOpen, mode, setIsCreating, fetchWorkspaces]);

  useEffect(() => {
    if (!isOpen) return;
    if (mode !== "create") return;
    const defaultBusiness = getDefaultAeraBusinessOption();
    setCreateTags([
      { key: "status", value: "active" },
      { key: "scale", value: "Solo" },
      { key: "business_type_config", value: defaultBusiness.config },
      { key: "business_type_display", value: defaultBusiness.label },
    ]);
  }, [isOpen, mode]);

  useEffect(() => {
    if (!isOpen) {
      editHydratedWorkspaceIdRef.current = null;
      return;
    }
    if (mode !== "edit") return;
    const ws = workspaces[0] ?? null;
    if (!ws?.id) return;
    if (editHydratedWorkspaceIdRef.current === ws.id) return;
    editHydratedWorkspaceIdRef.current = ws.id;

    setEditName(ws.name ?? "");
    setEditAvatar(
      resolveWorkspaceAvatarFromWorkspace({
        imageUrl: ws?.imageUrl ?? null,
        tags: ws?.tags,
        resolveImageUrl: resolveWorkspaceImageDisplayUrl,
      }),
    );
    const initialTags = Array.isArray(ws?.tags)
      ? ws!
          .tags!.map((t) => ({
            id: typeof t?.id === "string" ? t.id : undefined,
            key: String(t?.key ?? "").trim(),
            value: String(t?.value ?? "").trim(),
          }))
          .filter((t) => t.key.length > 0)
      : [];

    // Ensure status tag always exists (default: active)
    if (!initialTags.some((t) => t.key === "status")) {
      initialTags.push({ id: undefined, key: "status", value: "active" });
    }

    setEditTags(dedupeWorkspaceTags(initialTags));
    setError(null);
  }, [isOpen, mode, workspaces]);

  useEffect(() => {
    if (!isOpen) return;
    if (mode !== "edit") return;
    const ws = workspaces[0] ?? null;
    const ownerId = ws?.ownerId ?? null;
    if (!ownerId) {
      setOwnerProfile(null);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        setOwnerProfileLoading(true);

        const tryNormalize = (data: unknown) => {
          const user = extractOwnerUser(data);
          const displayNameRaw =
            user?.profile?.displayName ??
            user?.displayName ??
            user?.email ??
            user?.name ??
            ownerId;
          const avatarRaw =
            user?.profile?.avatar ?? user?.avatar ?? user?.image ?? null;
          return {
            displayName: String(displayNameRaw),
            avatarUrl: resolveWorkspaceImageDisplayUrl(
              avatarRaw ? String(avatarRaw) : null,
            ),
          };
        };

        // Try app API first
        try {
          const data: unknown = await usersApi.getUserById(ownerId);
          if (cancelled) return;
          setOwnerProfile(tryNormalize(data));
          return;
        } catch {
          // fall through to market-api
        }

        // Fallback: market-api user endpoint(s)
        const marketBase = (process.env.NEXT_PUBLIC_API_BASE_URL ?? "").replace(
          /\/+$/,
          "",
        );
        const candidates = [
          // First, try relative API (uses NEXT_PUBLIC_API_BASE_URL via ApiFetch)
          `/users/${ownerId}`,
          `/users/${ownerId}/`,
          // Then try market-api directly (will be proxied by ApiFetch)
          `${marketBase}/users/${ownerId}`,
          `${marketBase}/users/${ownerId}/`,
          `${marketBase}/user/${ownerId}`,
          `${marketBase}/user/${ownerId}/`,
        ];

        let lastErr: unknown = null;
        for (const url of candidates) {
          try {
            const j = await apiGet(url as string);
            if (cancelled) return;
            setOwnerProfile(tryNormalize(j));
            return;
          } catch (e) {
            lastErr = e;
          }
        }

        console.warn("Failed to load owner profile", { ownerId, lastErr });
        if (!cancelled) {
          setOwnerProfile({ displayName: ownerId, avatarUrl: null });
        }
      } finally {
        if (!cancelled) setOwnerProfileLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, mode, workspaces]);

  const handleSaveEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    const workspaceId = initialWorkspaceId ?? null;
    if (!workspaceId) return;
    if (!editName.trim()) return;

    try {
      setSavingEdit(true);
      setError(null);

      const current = workspaces[0];
      const original = workspaces[0] ?? null;
      let uploadedUrl: string | null = null;

      if (editAvatar.mode === "image" && editAvatar.file) {
        const uploadRes = await uploadApi.uploadImage(
          editAvatar.file,
          undefined,
          "thumbnails",
        );
        uploadedUrl =
          uploadRes.url || uploadRes.imageUrl || uploadRes.file?.url || null;
      }

      const imageUrl = resolveWorkspaceImageUrlForSave(editAvatar, {
        existingUrl: current?.imageUrl ?? null,
        uploadedUrl,
      });

      const nextName = editName.trim();
      const originalName = (original?.name ?? "").trim();
      const originalImage = original?.imageUrl ?? null;
      const shouldUpdateWorkspace =
        nextName !== originalName || imageUrl !== originalImage;

      if (shouldUpdateWorkspace) {
        await workspacesApi.update(workspaceId, {
          name: nextName,
          imageUrl,
        });
      }

      const normalizedTags = editTags
        .map((t) => ({ id: t.id, key: t.key.trim(), value: t.value.trim() }))
        .filter((t) => t.key.length > 0);

      for (const key of UNIQUE_WORKSPACE_TAG_KEYS) {
        const tag = normalizedTags.find((t) => t.key === key);
        if (!tag?.value) continue;
        await upsertWorkspaceTagByKey(
          workspaceId,
          key,
          tag.value,
          original?.tags ?? [],
        );
      }

      for (const t of normalizedTags.filter(
        (tag) => !UNIQUE_WORKSPACE_TAG_KEYS.has(tag.key),
      )) {
        if (t.id) {
          const originalValue =
            (original?.tags ?? []).find((x) => x.id === t.id)?.value ?? "";
          if (String(originalValue ?? "") === t.value) continue;
          const originalKey =
            (original?.tags ?? []).find((x) => x.id === t.id)?.key ?? t.key;
          const wouldDuplicate = (original?.tags ?? []).some(
            (x) =>
              String(x.id ?? "") !== String(t.id) &&
              String(x.key ?? "") === String(originalKey ?? "") &&
              String(x.value ?? "") === t.value,
          );
          if (wouldDuplicate) {
            // Another tag already has the same key+value; updating would 409.
            continue;
          }
          try {
            await workspacesApi.tags.update(workspaceId, t.id, {
              value: t.value,
            });
          } catch (err) {
            if (isNoChangesError(err) || isDuplicateKeyError(err)) continue;
            throw err;
          }
        } else {
          // Avoid creating duplicates if key+value already exists
          const exists = (original?.tags ?? []).some(
            (x) =>
              String(x.key ?? "") === t.key &&
              String(x.value ?? "") === t.value,
          );
          if (exists) continue;
          await workspacesApi.tags.create(workspaceId, {
            key: t.key,
            value: t.value,
          });
        }
      }

      await syncWorkspaceAvatarTagsFromWorkspace(
        workspaceId,
        editAvatar,
        normalizedTags,
        original?.tags,
      );

      await fetchWorkspaces();
      if (onSuccess) onSuccess(workspaceId);
    } catch (err) {
      console.error("Failed to update workspace:", err);
      setError("แก้ไขเวิร์กสเปซไม่สำเร็จ กรุณาลองใหม่อีกครั้ง");
    } finally {
      setSavingEdit(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      onExited={() => {
        editHydratedWorkspaceIdRef.current = null;
      }}
      size="md"
      sheetHost
      title={mode === "create" ? "สร้างเวิร์กสเปซ" : "ตั้งค่าเวิร์กสเปซ"}
      bodyClassName="p-6"
      bodyLoading={mode === "edit" && loading}
      bodyLoadingContent={<WorkspaceModalSkeleton mode={mode} />}
      footer={
        <div className="w-full space-y-3">
          <div className="flex gap-2 sm:gap-3">
            <AeraButton
              variant="outline"
              fontWeight="medium"
              className="shrink-0 min-w-22 px-5"
              onClick={() => {
                onClose();
              }}
            >
              ยกเลิก
            </AeraButton>
            <AeraButton
              variant="primary"
              fontWeight="medium"
              className="flex-1"
              type="submit"
              form="workspace-modal-form"
              loading={
                mode === "create"
                  ? creatingLoading || uploadingImage
                  : savingEdit
              }
              disabled={
                loading ||
                (mode === "create"
                  ? creatingLoading ||
                    !newWorkspaceName.trim() ||
                    !getCreateTagValue("business_type_config").trim()
                  : savingEdit ||
                    !(initialWorkspaceId ?? null) ||
                    !editName.trim())
              }
            >
              {mode === "create"
                ? creatingLoading || uploadingImage
                  ? "กำลังสร้าง..."
                  : "สร้าง"
                : savingEdit
                  ? "กำลังบันทึก..."
                  : "บันทึก"}
            </AeraButton>
          </div>
          {mode === "create" ? <WorkspaceCreatePrivacyNote /> : null}
        </div>
      }
    >
      {error && (
        <div className="mb-4 p-3 bg-aera-redlight text-aera-red text-sm rounded-lg border border-aera-red/20">
          {error}
        </div>
      )}

      {mode === "edit" && !error && workspaces.length === 0 && (
        <div className="mb-4 p-3 bg-gray-50 text-aera-slate text-sm rounded-lg border border-gray-100">
          ไม่พบข้อมูลเวิร์กสเปซ
        </div>
      )}

      <form
        id="workspace-modal-form"
        onSubmit={
          mode === "create"
            ? (e) => {
                e.preventDefault();
                if (!getCreateTagValue("business_type_config").trim()) {
                  setError("กรุณาเลือกรูปแบบธุรกิจของคุณ");
                  return;
                }
                void handleCreateWorkspace(e);
              }
            : handleSaveEdit
        }
        className=""
      >
        <WorkspaceNameIconField
          wrapperClassName="mb-6"
          label={mode === "edit" ? WORKSPACE_NAME_ICON_EDIT_LABEL : undefined}
          name={mode === "create" ? newWorkspaceName : editName}
          onNameChange={(value) =>
            mode === "create" ? setNewWorkspaceName(value) : setEditName(value)
          }
          avatar={mode === "create" ? createAvatar : editAvatar}
          onAvatarChange={mode === "create" ? setCreateAvatar : setEditAvatar}
          autoFocus
          required
        />

        {mode === "create" ? (
          <WorkspaceCreateOptionsSection
            selectedBusinessTypeId={selectedCreateBusinessType}
            onBusinessSelect={(opt) => {
              setCreateTagValue("business_type_config", opt.config);
              setCreateTagValue("business_type_display", opt.label);
              setError(null);
            }}
            scale={getCreateTagValue("scale")}
            onScaleChange={(scale) => setCreateTagValue("scale", scale)}
            status={getCreateTagValue("status")}
            onStatusChange={(status) => setCreateTagValue("status", status)}
          />
        ) : null}

        {mode === "edit" ? (
          <WorkspaceCreateOptionsSection
            selectedBusinessTypeId={selectedEditBusinessType}
            onBusinessSelect={(opt) => {
              setTagValue("business_type_config", opt.config);
              setTagValue("business_type_display", opt.label);
              setError(null);
            }}
            onClearError={() => setError(null)}
            scale={getTagValue("scale")}
            onScaleChange={(scale) => setTagValue("scale", scale)}
            status={getTagValue("status")}
            onStatusChange={(status) => setTagValue("status", status)}
            // showStatusPicker
          />
        ) : null}
      </form>

      {mode === "edit" && (initialWorkspaceId ?? null) && workspaces[0]?.id ? (
        <WorkspaceEditSummaryCard
          name={editName || workspaces[0].name || ""}
          avatar={editAvatar}
          createdAt={formatDateTime(workspaces[0].createdAt ?? null)}
          updatedAt={formatDateTime(
            resolveLatestWorkspaceUpdatedAt(workspaces[0]),
          )}
          ownerDisplayName={
            ownerProfile?.displayName ?? workspaces[0].ownerId ?? null
          }
          ownerAvatarUrl={ownerProfile?.avatarUrl ?? null}
          ownerLoading={ownerProfileLoading}
          statusLabel={formatStatusLabel(getTagValue("status"))}
        />
      ) : null}
    </Modal>
  );
}
